import React, { CSSProperties, FC, HTMLAttributes, Key, ElementType, useMemo } from 'react';
import { useTheme } from '@mui/material';

interface RenderHighlightedOptionProps {
    label: string;
    inputValue: string;
    sx?: CSSProperties;
    props?: HTMLAttributes<HTMLElement> & {
        key: Key;
    };
    component?: ElementType;
}

const escapeRegex = (str: string): string => {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

const RenderHighlightedOption: FC<RenderHighlightedOptionProps> = ({ props, sx, label, inputValue }) => {
    const theme = useTheme();
    const highlightStyle = theme.customStyles?.highlightText;

    const parts = useMemo(() => {
        if (!inputValue) {
            return [{ text: label, highlight: false }];
        }

        const escapedInput = escapeRegex(inputValue);
        const regex = new RegExp(`(${escapedInput})`, 'gi');
        const matches = Array.from(label.matchAll(regex));

        if (matches.length === 0) {
            return [{ text: label, highlight: false }];
        }

        const result: Array<{ text: string; highlight: boolean }> = [];
        let lastIndex = 0;

        matches.forEach((match) => {
            if (match.index !== undefined) {
                if (match.index > lastIndex) {
                    result.push({ text: label.slice(lastIndex, match.index), highlight: false });
                }
                result.push({ text: match[0], highlight: true });
                lastIndex = match.index + match[0].length;
            }
        });

        if (lastIndex < label.length) {
            result.push({ text: label.slice(lastIndex), highlight: false });
        }

        return result;
    }, [label, inputValue]);

    return (
        <span style={{ ...sx, display: 'inline-block', width: '100%' }} {...props}>
            {parts.map((part, index) => (
                <span
                    key={index}
                    style={
                        part.highlight
                            ? {
                                  ...highlightStyle,
                                  display: 'inline',
                              }
                            : {
                                  display: 'inline',
                              }
                    }
                >
                    {part.text}
                </span>
            ))}
        </span>
    );
};

export default RenderHighlightedOption;
